/**
 * Given a Lottie JSON string, replaces all instances of a color with another.
 *
 * Either color may be given as a 6-digit hex, like `#FF0000`, or an rgb, like `255 0 0`.
 */
export default function replaceLottieColor(
  src: string,
  fromColor: string,
  toColor: string,
  tolerance = 0.05
) {
  const fromColorChannels = colorToLottieChannels(fromColor);
  const toColorChannels = colorToLottieChannels(toColor);

  let out = src;

  const replacer = (match, r, g, b, a) => {
    const partChannels = [
      parseFloat(r),
      parseFloat(g),
      parseFloat(b),
      parseFloat(a)
    ];
    const rgbChannels = partChannels.slice(0, 3);
    const diff = rgbChannels.map((c, i) => Math.abs(c - fromColorChannels[i]));
    if (diff.every(d => d <= tolerance)) {
      return `[${toColorChannels.slice(0, 3).join(',')},${a}]`;
    }
    return match;
  };

  // [r,g,b,a]
  out = out.replace(
    /\[(\d+(?:\.\d+)?),(\d+(?:\.\d+)?),(\d+(?:\.\d+)?),(\d+(?:\.\d+)?)\]/g,
    replacer
  );

  // [r,g,b]
  out = out.replace(
    /\[(\d+(?:\.\d+)?),(\d+(?:\.\d+)?),(\d+(?:\.\d+)?)\]/g,
    replacer
  );

  return out;
}

function hexToRgba(hex: string): number[] {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const a = 1;
  return [r, g, b, a];
}

/**
 * Converts CSS color formats to Lottie color channels.
 *
 * @param hexOrRgb A hex color, like `#FF0000`, or an rgb string, like `255 0 0`.
 * @returns An array of 4 numbers, like `[1, 0, 0, 1]`.
 */
function colorToLottieChannels(hexOrRgb: string): number[] {
  return hexOrRgb.startsWith('#')
    ? hexToRgba(hexOrRgb)
    : hexOrRgb.split(' ').map(c => parseFloat(c) / 255);
}
